import { useEffect, useState } from 'react'
import { doc } from 'firebase/firestore'
import { onSnapshot } from '@/lib/metered'
import { db } from '@/lib/firebase'

export type ProjectSummary = {
  hoursLogged: number
  gateStatus: Record<string, 'Open' | 'At Risk' | 'Cleared'>
  updatedAt?: { seconds: number }
}

/* No current week, phases in flight or working-on list here. Each is worked out from the
   live registers — see `weekOn` and `home/actionItems` — so any copy still sitting in the
   summary document is ignored rather than trusted. */
const FALLBACK: ProjectSummary = {
  hoursLogged: 38,
  gateStatus: {},
}

/**
 * The live summary Milestone maintains. Falls back to the last known baseline rather than
 * rendering zeros — a client looking at "0 hours" would reasonably conclude nothing has
 * happened, which is worse than a slightly stale number.
 */
export function useProjectSummary() {
  const [summary, setSummary] = useState<ProjectSummary>(FALLBACK)
  const [loading, setLoading] = useState(true)
  const [live, setLive] = useState(false)

  useEffect(
    () =>
      onSnapshot(
        doc(db, 'project', 'summary'),
        (snap) => {
          if (snap.exists()) {
            setSummary({ ...FALLBACK, ...(snap.data() as Partial<ProjectSummary>) })
            setLive(true)
          }
          setLoading(false)
        },
        () => setLoading(false),
      ),
    [],
  )

  return { summary, loading, live }
}

export function daysUntil(dateIso: string, from = new Date()): number {
  const target = new Date(`${dateIso}T00:00:00`)
  const ms = target.getTime() - new Date(from.toDateString()).getTime()
  return Math.round(ms / 86_400_000)
}
